import { FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import SelectTextField from '../../../molecules/SelectTextField';
import BeneficiarySelectField from './BeneficiarySelectField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { Beneficiary, SelectOption } from '../../../../types/LegalCase';
import type { ErrorField } from '../../../../types/ErrorField';
import useLegalCaseExpenseForm, { residualErrors } from '../../../../hooks/useLegalCaseExpenseForm';
import type { AddCaseExpenseValue } from '../../../../hooks/useLegalCaseExpenses';

interface LegalCaseExpenseFormProps {
    categoryOptions: SelectOption[];
    beneficiaries: Beneficiary[];
    beneficiaryTypeOptions: SelectOption[];
    onCreateBeneficiary: (
        data: { name: string; beneficiaryType?: string },
    ) => Promise<Beneficiary | null>;
    beneficiaryErrorFields: ErrorField[];
    isBeneficiarySaving: boolean;
    defaultBeneficiaryId?: string;
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: AddCaseExpenseValue) => Promise<boolean>;
    onCancel: () => void;
}

// Formulário de despesa da seção Custos do caso. Só JSX + bindings: estado e regras vivem em
// useLegalCaseExpenseForm. O beneficiário usa o select com "+ Novo" inline.
const LegalCaseExpenseForm = ({
    categoryOptions,
    beneficiaries,
    beneficiaryTypeOptions,
    onCreateBeneficiary,
    beneficiaryErrorFields,
    isBeneficiarySaving,
    defaultBeneficiaryId,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCaseExpenseFormProps) => {
    const form = useLegalCaseExpenseForm({
        onSubmit,
        defaultCategory: categoryOptions[0]?.value || 'OTHER',
        defaultBeneficiaryId,
    });

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <SelectTextField
                    label="Categoria"
                    items={categoryOptions}
                    value={form.category}
                    onChange={(e) => form.setCategory(e.target.value)}
                    emptyLabel="Selecione a categoria..."
                    errorMessage={getErrorMessage(errorFields, 'category')}
                />
                <CurrencyInput
                    label="Valor"
                    prefix="R$"
                    fractionDigits={2}
                    value={form.amount}
                    onChange={form.setAmount}
                    errorMessage={getErrorMessage(errorFields, 'amount')}
                />
            </div>

            <DateField
                label="Data de pagamento (opcional)"
                value={form.paidAt}
                onChange={form.setPaidAt}
                errorMessage={getErrorMessage(errorFields, 'paidAt')}
            />

            <BeneficiarySelectField
                value={form.beneficiaryId}
                onChange={form.setBeneficiaryId}
                beneficiaries={beneficiaries}
                beneficiaryTypeOptions={beneficiaryTypeOptions}
                onCreate={onCreateBeneficiary}
                isSaving={isBeneficiarySaving}
                errorFields={beneficiaryErrorFields}
            />

            <TextEdit
                label="Descrição (opcional)"
                value={form.description}
                onChange={(e) => form.setDescription(e.target.value)}
                errorMessage={getErrorMessage(errorFields, 'description')}
            />

            {/* Erros de validação (422) sem campo próprio na tela — não deixa sumir. */}
            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={form.submit}
                    disabled={isSaving || !form.canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Registrar despesa'}
                </button>
            </div>
        </div>
    );
};

export default LegalCaseExpenseForm;
